import { Grid, Typography } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import { TimeOnlyWithZ } from "../../../../types/date-time-types";
import { Modal, TimePickerModal } from "../../../Modals";
import LoadingSpinner from "../../../Utilities/LoadingSpinner";
import { DefaultAvailabilityEvent } from "./defaultAvailabilityApi";
import useDefaultAvailability from "./useDefaultAvailability";

const DefaultAvailability: React.FC = () => {
  const { t } = useTranslation();
  const { defaultAvailability, saveDefaultAvailability, isLoading, error } =
    useDefaultAvailability();

  const [selectedItem, setSelectedItem] = React.useState<DefaultAvailabilityEvent | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = React.useState<boolean>(false);
  const [isTimepickerNotAvailableSwitchToggled, setIsTimepickerNotAvailableSwitchToggled] =
    React.useState(false);

  const handleItemClick = (availabilityItem: DefaultAvailabilityEvent) => {
    setIsTimepickerNotAvailableSwitchToggled(!availabilityItem.is_available);
    setSelectedItem(availabilityItem);
  };

  const handleTimePickerNotAvailableSwitchToggle = () => {
    setIsTimepickerNotAvailableSwitchToggled(!isTimepickerNotAvailableSwitchToggled);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const is_available = !isTimepickerNotAvailableSwitchToggled;
    const start = formData.get("from") as string;
    // Backend requires end time to equal start time for "not available"
    const end = is_available ? (formData.get("to") as string) : start;

    const updatedDefaultAvailability = {
      ...selectedItem!,
      is_available,
      // Add "Z" so we have the same time format as backend sends and accepts, e.g. "08:00Z"
      start: start + "Z",
      end: end + "Z",
    };

    saveDefaultAvailability(updatedDefaultAvailability);

    handleTimePickerClose();
  };

  const handleTimePickerClose = () => {
    setSelectedItem(null);
  };

  React.useEffect(() => {
    setIsErrorModalOpen(true);
  }, [error]);

  const formatDay = (day: string) => {
    switch (day) {
      case "MON":
        return t("Monday");
      case "TUE":
        return t("Tuesday");
      case "WED":
        return t("Wednesday");
      case "THU":
        return t("Thursday");
      case "FRI":
        return t("Friday");
      case "SAT":
        return t("Saturday");
      case "SUN":
        return t("Sunday");
      default:
        return;
    }
  };

  // Remove the "Z" we recieve from backend, for example: "08:00Z" -> "08:00"
  const formatAvailabilityTime = (time: TimeOnlyWithZ) => time.replace("Z", "");

  const renderAvailabilityTime = (availabilityEvent: DefaultAvailabilityEvent) => {
    if (!availabilityEvent.is_available) {
      return t("Not available");
    }

    const formattedStartTime = formatAvailabilityTime(availabilityEvent.start);
    const formattedEndTime = formatAvailabilityTime(availabilityEvent.end);
    return `${formattedStartTime} - ${formattedEndTime}`;
  };

  const defaultAvailabilityList = defaultAvailability.map((availabilityItem) => (
    <Grid
      container
      spacing={3}
      key={availabilityItem.id}
      id={availabilityItem.id}
      onClick={() => handleItemClick(availabilityItem)}
      style={{ cursor: "pointer" }}
    >
      <Grid item xs={6}>
        <Typography variant="body1">{formatDay(availabilityItem.name)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" component="span">
          {renderAvailabilityTime(availabilityItem)}
          <ChevronRight />
        </Typography>
      </Grid>
    </Grid>
  ));

  return (
    <>
      <Typography variant="subtitle1" gutterBottom style={{ marginTop: 0 }}>
        {t("Default availability")}
      </Typography>
      <hr />

      {isLoading && (
        <div style={{ textAlign: "center" }}>
          <LoadingSpinner />
        </div>
      )}

      {defaultAvailabilityList}

      {selectedItem && (
        <TimePickerModal
          show={true}
          onHide={handleTimePickerClose}
          onSubmit={handleSubmit}
          toggle={isTimepickerNotAvailableSwitchToggled}
          onToggle={handleTimePickerNotAvailableSwitchToggle}
          currentItem={{ start: selectedItem.start, end: selectedItem.end }}
          title={t("Availability")}
        />
      )}

      {error && (
        <Modal
          open={isErrorModalOpen}
          message={t(error.error_message)}
          title={t("Error")}
          onClose={() => {
            setIsErrorModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default DefaultAvailability;
